import React from 'react';
import { useSurfaceIsDark } from '@/hooks/useSurfaceInk';
import TereikoLogoText from '../../../public/logos/TereikoLogoText.png';
import TereikoLogoTextDark from '../../../public/logos/TereikoLogoTextDark.png';
import { InstagramIcon, LinkedInIcon, XIcon } from '../icons/SocialIcons';
import { useAuth } from '../../hooks/useAuth';
import { useImageUrl } from '../../services/uploads';
import { openCookiePreferences } from '@/lib/cookieConsent';
import { LEGAL_DOCS } from '@/config/legal';

/**
 * Shared site footer — identical on every surface (management, learner,
 * course player). Keeps the light/dark gradient backdrop and copyright bar,
 * and layers on the brand + social + Legal/Contact blocks from the redesign.
 * Legal links render for everyone — learners are data subjects too, so the
 * Privacy and Cookies policies (and the T&C their admin accepted on their
 * behalf) must be reachable from the learner app as well.
 */
const SOCIAL_LINKS = [
  { name: 'Instagram', href: 'https://www.instagram.com/tereiko_lmp', Icon: InstagramIcon },
  { name: 'LinkedIn', href: 'https://www.linkedin.com/company/tereiko', Icon: LinkedInIcon },
  { name: 'X', href: 'https://x.com/tereikoLMP', Icon: XIcon },
] as const;

/** Two short columns rather than one long line of links (Aleix, 2026-09-10):
 *  the agreements on the left, everything cookie-related on the right. */
const LEGAL_COLUMNS = [
  [
    { label: 'Terms and Conditions', href: LEGAL_DOCS.termsAndConditions },
    { label: 'Privacy Policy', href: LEGAL_DOCS.privacyPolicy },
  ],
  [{ label: 'Cookies Policy', href: LEGAL_DOCS.cookiesPolicy }],
] as const;

const CONTACT_EMAIL = 'contact@tereiko.com';

const Footer: React.FC = () => {
  const { activeMembership } = useAuth();
  // Which wordmark to show follows the BAND, not the theme: a bright workspace
  // colour makes the footer wash light even in dark mode, and the white
  // wordmark would vanish on it (Aleix, 2026-09-11). Brand artwork is never
  // recoloured — only the right variant is chosen.
  const bandIsDark = useSurfaceIsDark('band');
  const logo = bandIsDark ? TereikoLogoTextDark : TereikoLogoText;
  // Enterprise white label: the customer's identity replaces the Tereiko
  // brand block. Legal links and the company notice stay — they are ours.
  const styling = activeMembership?.tenant.styling;
  const whiteLabel = Boolean(styling?.white_label);
  const [brandLogoUrl] = useImageUrl(whiteLabel ? (styling?.logo_key ?? null) : null);

  return (
    <footer className="mt-auto">
      {/* Brand + links band — sits on the existing light/dark gradient. */}
      <div className="relative overflow-hidden">
        <div
          aria-hidden="true"
          className="brand-gradient absolute inset-0 pointer-events-none"
        />
        <div className="relative px-6 sm:px-8 lg:px-16 py-4">
          <div className="flex flex-col items-center gap-3 text-center text-band-ink md:flex-row md:items-center md:justify-between md:gap-8 md:text-left">
            {/* Brand: the workspace's own identity under white label,
                otherwise the Tereiko logo + social row */}
            <div className="flex flex-row items-center gap-3">
              {whiteLabel ? (
                <div className="flex items-center gap-3">
                  {brandLogoUrl && (
                    <img
                      src={brandLogoUrl}
                      alt=""
                      className="h-8 w-8 object-contain rounded"
                    />
                  )}
                  <span className="text-lg font-bold">
                    {activeMembership?.tenant.name}
                  </span>
                </div>
              ) : (
                <>
                  <img src={logo} alt="Tereiko" className="h-7 object-contain" />
                  <ul className="flex items-center gap-0.5">
                    {SOCIAL_LINKS.map(({ name, href, Icon }) => (
                      <li key={name}>
                        <a
                          href={href}
                          target="_blank"
                          rel="noreferrer noopener"
                          aria-label={name}
                          className="flex items-center justify-center rounded-full p-1.5 transition-colors duration-fast hover:bg-black/5 dark:hover:bg-white/10 focus-ring active:scale-95"
                        >
                          <Icon className="h-[18px] w-[18px]" />
                        </a>
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </div>

            {/* Legal + Contact — on every surface (learners included). Two
                short columns, agreements and cookies, with the contact address
                beside them (Aleix, 2026-09-10). */}
            <div className="flex flex-wrap items-start justify-center gap-x-10 gap-y-3 md:justify-end">
              <nav
                className="flex items-start gap-x-10"
                aria-label="Legal"
              >
                {LEGAL_COLUMNS.map((column, index) => (
                  <div
                    key={column[0].label}
                    className="flex flex-col items-center gap-1 md:items-start"
                  >
                    {column.map(({ label, href }) => (
                      <a
                        key={label}
                        href={href}
                        target="_blank"
                        rel="noreferrer noopener"
                        className="text-sm font-light underline underline-offset-2 transition-opacity duration-fast opacity-80 hover:opacity-100 focus-ring rounded-sm"
                      >
                        {label}
                      </a>
                    ))}
                    {/* The preferences dialog belongs with the cookies policy. */}
                    {index === LEGAL_COLUMNS.length - 1 && (
                      <button
                        type="button"
                        onClick={openCookiePreferences}
                        className="text-sm font-light underline underline-offset-2 transition-opacity duration-fast opacity-80 hover:opacity-100 focus-ring rounded-sm"
                      >
                        Cookie preferences
                      </button>
                    )}
                  </div>
                ))}
              </nav>

              <a
                href={`mailto:${CONTACT_EMAIL}`}
                className="text-sm font-light transition-opacity duration-fast opacity-80 hover:opacity-100 hover:underline underline-offset-2 focus-ring rounded-sm"
              >
                {CONTACT_EMAIL}
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Copyright bar — unchanged colors. */}
      <div className="flex items-center justify-center h-9 bg-surface-primary">
        <p className="text-xs text-text-primary">
          &copy; All rights reserved. TEREIKO LMP, 2026.
        </p>
      </div>
    </footer>
  );
};

export default Footer;
